/**
 * AdminActivityPage — Admin-only monitoring and observability panel.
 *
 * Tabs:
 *   - Overview: summary cards + activity trends + live infra snapshot (polls /live every 5s)
 *   - Users: per-user activity leaderboard
 *   - Compute: job stats by kind + daily chart
 *   - Market Data: cache totals (reuses getDataCacheStats)
 *   - Security: failed-auth IPs, hourly histogram, MCP errors
 *
 * Access gated to admin role (mirrors AdminDataCachePage pattern exactly).
 */

import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore.js';
import { OverviewTab } from './OverviewTab.js';
import { UsersTab } from './UsersTab.js';
import { ComputeTab } from './ComputeTab.js';
import { MarketDataTab } from './MarketDataTab.js';
import { SecurityTab } from './SecurityTab.js';

// ============================================================================
// Types
// ============================================================================

type TabId = 'overview' | 'users' | 'compute' | 'market' | 'security';

// ============================================================================
// AdminActivityPage
// ============================================================================

export function AdminActivityPage() {
  const user = useAuthStore((s) => s.user);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // ---- Access denied ----
  if (!user || user.role !== 'admin') {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <p className="text-gray-300 font-medium">Access denied</p>
          <p className="text-gray-500 text-sm mt-1">
            This page is only accessible to administrators.
          </p>
        </div>
      </div>
    );
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'compute', label: 'Compute' },
    { id: 'market', label: 'Market Data' },
    { id: 'security', label: 'Security' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-white">Activity & Observability</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Platform monitoring, user activity, and infrastructure health
          </p>
        </div>
      </div>

      {/* Tabs bar */}
      <div className="border-b border-gray-700 px-6 flex-shrink-0">
        <div className="flex gap-1 flex-wrap -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-gray-400 hover:text-white hover:border-gray-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'compute' && <ComputeTab />}
          {activeTab === 'market' && <MarketDataTab />}
          {activeTab === 'security' && <SecurityTab />}
        </div>
      </div>
    </div>
  );
}
